import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, onSnapshot,
  query, orderBy, where, serverTimestamp
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useOrganization } from '../context/OrganizationContext';
import { waCloudAPI, campaignsAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import { useTranslation } from 'react-i18next';
import {
  NIVEL, MOTIVO_BLOQUEO, nivelDePlantilla, esSeleccionable, motivoDeBloqueo,
  motivoDeRechazo, puntuacionDeCalidad,
} from '../utils/estadoPlantilla';
import CrearPlantilla from '../componentes/plantillas/CrearPlantilla';
import { destinatarioDesdeChat, destinatarioDesdeCsv } from '../utils/destinatarioCampana';
import { cuentaDelChat, cuentaDePnid } from '../utils/cuentaDelChat';
import { puedeImportarContactos, FINALIDADES_UI } from '../utils/declaracionContactos';
import {
  camposDisponibles, revisarMapeo, componentesParaDestinatario,
} from '../utils/variablesCampana';

const STATUS_CONFIG = {
  borrador: { labelKey: 'statuses.borrador', color: '#6c757d', bg: '#e2e3e5' },
  programada: { labelKey: 'statuses.programada', color: '#0c5460', bg: '#d1ecf1' },
  enviando: { labelKey: 'statuses.enviando', color: '#856404', bg: '#fff3cd' },
  completada: { labelKey: 'statuses.completada', color: '#155724', bg: '#d4edda' },
  pausada: { labelKey: 'statuses.pausada', color: '#721c24', bg: '#f8d7da' },
  // `interrumpida` NO es `completada`: la ejecución murió a mitad y parte de los
  // destinatarios no recibió nada. Se pinta en ámbar de aviso —ni verde de éxito ni
  // rojo de error— porque lo que salió, salió bien; lo que falta es lo que el operador
  // tiene que decidir. NO se reintenta sola: relanzarla duplicaría a quien ya recibió.
  interrumpida: { labelKey: 'statuses.interrumpida', color: '#8a6d00', bg: '#fef7e0' },
  // La campaña que no arrancó por su plantilla: no se envió NADA, así que tampoco es
  // un fallo de entrega.
  bloqueada: { labelKey: 'statuses.bloqueada', color: '#5f6368', bg: '#e8eaed' }
};

export default function Campañas() {
  const { context, loading: contextLoading } = useOrganization();
  const [uid, setUid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [view, setView] = useState('list'); // list, create, detail
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  // El estado de ENTREGA de cada envío, contado al abrir el detalle. Ver el efecto.
  const [entregas, setEntregas] = useState({ cargando: false, fallo: false, porWamid: {}, conteo: {} });

  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [campaignName, setCampaignName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [numerosApi, setNumerosApi] = useState([]);
  const [selectedNumero, setSelectedNumero] = useState(null);

  // Variable mapping
  const [varMapping, setVarMapping] = useState({});

  // Recipients
  const [recipientMode, setRecipientMode] = useState('contacts'); // contacts, csv
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [csvRecipients, setCsvRecipients] = useState([]);
  const [csvDescartadas, setCsvDescartadas] = useState([]);
  // La casilla de la declaración. Sin marcar, los contactos entran SIN consentimiento
  // declarado y el filtro los trata como hoy. No hay valor por defecto.
  const [declaracionAceptada, setDeclaracionAceptada] = useState(false);
  // LAS FINALIDADES SE SELECCIONAN, NO SE ASUMEN. Arranca vacío a propósito: marcar
  // alguna por defecto sería declarar en nombre del negocio algo que no ha dicho.
  const [finalidades, setFinalidades] = useState([]);
  // Si la declaración no se registra, el operador TIENE que verlo aquí. Ver el catch.
  const [falloDeclaracion, setFalloDeclaracion] = useState(false);
  // Aceptar la declaración sin finalidades no significa nada — el texto dice «para las
  // finalidades seleccionadas». El backend lo rechaza igual; esto evita el viaje.
  const puedeImportar = puedeImportarContactos(declaracionAceptada, finalidades);
  const [declarando, setDeclarando] = useState(false);
  // El recuento del preview. `null` = aún no se pidió; `{error:true}` = falló y el
  // paso 3 sigue usable. Ver `pedirPreview`.
  const [preview, setPreview] = useState(null);
  const [previewCargando, setPreviewCargando] = useState(false);
  const csvInputRef = useRef(null);

  // Send options
  const [sendMode, setSendMode] = useState('now'); // now, schedule
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [launching, setLaunching] = useState(false);
  // El aviso de la ejecución, que llega DESPUÉS de volver a la lista porque el envío
  // no se espera. `info` y `error` son cosas distintas: ver el `catch` de abajo.
  const [avisoEjecucion, setAvisoEjecucion] = useState(null);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation('campaigns');
  const { t: tc } = useTranslation('common');
  const orgId = context.mode === 'organization' ? context.orgId : null;

  const getCampaignsPath = () => context.mode === 'organization' && context.orgId
    ? ['organizations', context.orgId, 'campaigns']
    : ['clientes', uid, 'campaigns'];

  const getChatsPath = () => context.mode === 'organization' && context.orgId
    ? ['organizations', context.orgId, 'chats']
    : ['clientes', uid, 'chats'];

  const getNumerosPath = () => context.mode === 'organization' && context.orgId
    ? ['organizations', context.orgId, 'numeros']
    : ['clientes', uid, 'numeros'];

  const getEnviosPath = () => context.mode === 'organization' && context.orgId
    ? ['organizations', context.orgId, 'envios']
    : ['clientes', uid, 'envios'];

  // El estado de entrega, CONTADO AL LEER y no acumulado en un contador.
  //
  // POR QUÉ NO UN CONTADOR EN LA CAMPAÑA: una campaña de 200 destinatarios genera ~400
  // acuses (`sent` + `delivered`) en unos minutos, y todos incrementarían el MISMO
  // documento. Firestore aguanta ~1 escritura por segundo sostenida sobre un documento;
  // por encima, las transacciones reintentan y algunas fallan, y el contador quedaría
  // POR DEBAJO del real — el fallo silencioso que este dato existe para evitar.
  //
  // Una consulta al abrir el detalle no tiene contención y el número es exacto por
  // construcción en vez de acumulado.
  //
  // NO SE PINTA EN LA LISTA a propósito: exigiría una consulta por fila para un dato que
  // se mira una vez. La lista muestra `enviados`, que sigue siendo verdad.
  useEffect(() => {
    if (view !== 'detail' || !selectedCampaign?.id || !uid) return;
    let vigente = true;
    setEntregas({ cargando: true, fallo: false, porWamid: {}, conteo: {} });
    (async () => {
      try {
        const q = query(collection(db, ...getEnviosPath()), where('campaignId', '==', selectedCampaign.id));
        const snap = await getDocs(q);
        if (!vigente) return;
        const porWamid = {};
        const conteo = {};
        snap.docs.forEach((d) => {
          const v = d.data();
          if (!v.wamid) return;
          // Se guarda el OBJETO, no sólo el estado: la exportación de resultados
          // necesita además la base jurídica, y viene en la misma consulta.
          porWamid[v.wamid] = { estado: v.estado || 'enviado', base: v.baseResumen || null };
          conteo[v.estado || 'enviado'] = (conteo[v.estado || 'enviado'] || 0) + 1;
        });
        setEntregas({ cargando: false, fallo: false, porWamid, conteo });
      } catch (err) {
        // Un fallo aquí NO puede vaciar el detalle: el resto de la campaña es válido.
        console.warn('Error cargando estados de entrega:', err?.code || err?.message);
        if (vigente) setEntregas({ cargando: false, fallo: true, porWamid: {}, conteo: {} });
      }
    })();
    return () => { vigente = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedCampaign?.id, uid, context.mode, context.orgId]);

  // Auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) { navigate('/'); return; }
      setUid(u.uid);
      setLoading(false);
    });
    return () => unsub();
  }, [navigate]);

  // Load campaigns
  useEffect(() => {
    if (!uid || contextLoading) return;
    const q = query(collection(db, ...getCampaignsPath()), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setCampaigns(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => {
      // Silently handle permission errors — rules may not be deployed yet
      console.warn('Error loading campaigns (may need Firestore rules update):', err.code || err.message);
      setCampaigns([]);
    });
    return () => unsub();
  }, [uid, contextLoading, context.mode, context.orgId]);

  // Load WA API numbers
  useEffect(() => {
    if (!uid || contextLoading) return;
    const unsub = onSnapshot(collection(db, ...getNumerosPath()), (snap) => {
      const apiNums = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(n => n.tipoConexion === 'api');
      setNumerosApi(apiNums);
      // Multicuenta (commit 9, §4): con MÁS de un número no hay número por defecto — el
      // selector arranca vacío y la audiencia se calcula para la cuenta elegida. Con uno,
      // se elige solo, como siempre.
      if (apiNums.length === 1 && !selectedNumero) {
        setSelectedNumero(apiNums[0]);
      }
    });
    return () => unsub();
  }, [uid, contextLoading, context.mode, context.orgId]);

  // Load templates when number is selected. Recibe el número porque, al cambiarlo en el
  // selector, el estado `selectedNumero` todavía es el anterior (9b: antes cargaba las
  // plantillas del número viejo).
  const loadTemplates = async (numero = selectedNumero) => {
    if (!numero?.phone_number_id || !numero?.waba_id) return;
    setLoadingTemplates(true);
    try {
      const result = await waCloudAPI.getTemplates({
        phoneNumberId: numero.phone_number_id,
        wabaId: numero.waba_id,
        organizationId: orgId
      });
      // Se listan TODOS los estados. El filtro a APPROVED que habia aqui escondia que
      // la plantilla existiera siquiera: una PENDING o una REJECTED desaparecia sin
      // explicacion y no habia forma de ver que le habia pasado. Quien decide si se
      // puede elegir es `nivelDePlantilla`, en el render.
      setTemplates(result.templates || []);
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Load contacts (chats) for recipient selection
  // Multicuenta (commit 9, §4): la AUDIENCIA es la de la cuenta del número elegido
  // (`cuentaDelChat`, la misma regla que el servidor): un chat que habló con el otro número
  // no entra, porque su alta (interacción previa, OFERTAS) es de esa cuenta y el servidor lo
  // bloquearía igual. Se pasa el número porque al cambiarlo en el selector el estado aún no
  // ha cambiado. Sin número elegido (más de uno y ninguno marcado): lista vacía.
  const loadContacts = async (numero = selectedNumero) => {
    try {
      const cuenta = cuentaDePnid(numero?.phone_number_id);
      if (!cuenta) { setContacts([]); return; }
      const snap = await getDocs(collection(db, ...getChatsPath()));
      // El destinatario sale de `telefono`, NO de `remitente`. `remitente` guarda el
      // NOMBRE, y usarlo aquí es lo que hizo salir el primer envío real con
      // `to: "Rodrigo"`. Los que no resuelven se conservan en la lista pero NO se
      // pueden seleccionar: esconderlos dejaría al usuario buscando un contacto que
      // ve en el inbox y no aparece aquí, sin saber por qué.
      const chatContacts = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(c => c.canal === 'whatsapp' && cuentaDelChat(c) === cuenta)
        .map(c => {
          const r = destinatarioDesdeChat(c);
          return {
            id: c.id,
            nombre: c.nombre || c.remitente || '',
            phone: r.ok ? r.destinatario.phone : null,
            motivo: r.ok ? null : r.motivo,
            estado: c.estado
          };
        });
      setContacts(chatContacts);
    } catch (err) {
      console.error('Error loading contacts:', err);
    }
  };

  const handleSelectTemplate = (tpl) => {
    // Segunda red: el render ya no pone `onClick` en las bloqueadas, pero si alguien
    // reordena el JSX mas adelante esto impide que una PENDING acabe seleccionada y
    // se lance una campana que Meta rechaza.
    if (!esSeleccionable(tpl?.status, tpl?.category, tpl?.components)) return;
    setSelectedTemplate(tpl);
    const bodyComp = tpl.components?.find(c => c.type === 'BODY');
    const bodyText = bodyComp?.text || '';
    const matches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
    const mapping = {};
    matches.forEach(m => {
      const num = m.replace(/[{}]/g, '');
      mapping[num] = { type: 'fixed', value: '' };
    });
    setVarMapping(mapping);
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) return;

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const phoneIdx = headers.findIndex(h => h.includes('telefono') || h.includes('phone') || h.includes('número'));
      const nameIdx = headers.findIndex(h => h.includes('nombre') || h.includes('name'));

      if (phoneIdx === -1) {
        alert('CSV debe tener una columna "telefono" o "phone"');
        return;
      }

      // Las filas que no resuelven NO se descartan en silencio. Un CSV de doscientos
      // contactos aceptado a medias sin avisar es peor que no tener CSV: el usuario
      // lanzaría creyendo que envía a doscientos.
      const recipients = [];
      const descartadas = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map(c => c.trim());
        const r = destinatarioDesdeCsv({
          telefono: cols[phoneIdx],
          nombre: nameIdx >= 0 ? cols[nameIdx] : '',
          extraFields: headers.reduce((acc, h, idx) => {
            if (idx !== phoneIdx && idx !== nameIdx) acc[h] = cols[idx] || '';
            return acc;
          }, {})
        });
        if (r.ok) recipients.push(r.destinatario);
        else descartadas.push({ fila: i + 1, valor: cols[phoneIdx] || '', motivo: r.motivo });
      }

      setCsvRecipients(recipients);
      setCsvDescartadas(descartadas);

      // La declaración se registra AL IMPORTAR, no al lanzar: es una afirmación sobre
      // estos contactos, y hacerla en el momento de la importación es lo que la
      // convierte en un acto consciente y no en un efecto colateral del envío.
      setFalloDeclaracion(false);
      if (puedeImportar && recipients.length > 0) {
        setDeclarando(true);
        campaignsAPI.declararConsentimiento({
          organizationId: orgId,
          telefonos: recipients.map((r) => r.phone),
          idioma: (i18n.language || 'es').slice(0, 2),
          declaracionAceptada: true,
          // Multicuenta (D7): el alta declarada vale para la cuenta del número elegido. El
          // servidor la exige cuando la org tiene más de un número de la Cloud API.
          accountId: cuentaDePnid(selectedNumero?.phone_number_id) || null,
          // Se mandan las marcadas, y el backend vuelve a validarlas: aquí no hay
          // protección, sólo comodidad.
          finalidades,
        }).catch((err) => {
          // EL FALLO TIENE QUE VERSE. Con sólo un `console.error`, el operador marcaba
          // las casillas, subía el CSV, veía «1 destinatario» y todo parecía correcto:
          // el 400 moría en la consola y descubría el problema TRES PASOS DESPUÉS, al
          // lanzar, con un mensaje que hablaba de otra cosa.
          //
          // Es la SEGUNDA vez con esta forma en este fichero: el 409 de «ya se está
          // ejecutando» también se tragaba en un `console.error` y la protección del
          // backend no tenía señal en pantalla. Una protección que nadie ve no protege
          // a nadie de nada.
          console.error('No se pudo registrar la declaración de consentimiento:', err);
          setFalloDeclaracion(true);
        }).finally(() => setDeclarando(false));
      }
    };
    reader.readAsText(file);
  };

  const getRecipients = () => {
    // SIN `replace`. El que había aquí borraba el '+' —`/[\s\-+()]/g`— justo antes de
    // enviar, así que un teléfono con prefijo explícito llegaba al backend sin él y
    // volvía a ser inválido. La limpieza de formato ocurre UNA vez, en
    // `destinatarioCampana`, y aquí ya no se toca.
    if (recipientMode === 'csv') return csvRecipients;
    return selectedContacts.map(c => ({ phone: c.phone, nombre: c.nombre }));
  };

  const getRecipientCount = () => {
    return recipientMode === 'csv' ? csvRecipients.length : selectedContacts.length;
  };

  const getTemplatePreview = () => {
    if (!selectedTemplate) return '';
    const bodyComp = selectedTemplate.components?.find(c => c.type === 'BODY');
    let text = bodyComp?.text || '';
    Object.entries(varMapping).forEach(([key, config]) => {
      const val = config.type === 'fixed' ? config.value : `[${config.value || 'campo'}]`;
      text = text.replace(`{{${key}}}`, val || `{{${key}}}`);
    });
    return text;
  };

  // SIN `|| ' '`. El que había aquí mandaba un ESPACIO cuando el mapeo estaba vacío:
  // un valor que finge que hay dato. El mensaje salía y le llegaba al cliente con un
  // hueco en blanco donde iba su nombre. Ahora la regla vive en `variablesCampana` y
  // devuelve `null` si algo no resuelve; el lanzamiento se bloquea antes.
  const buildRecipientComponents = (recipient) => componentesParaDestinatario(varMapping, recipient);

  // Los campos que el ORIGEN de destinatarios ofrece de verdad. Con contactos de la
  // lista es sólo `nombre`; con CSV, además las columnas de ese fichero.
  const camposParaVariables = camposDisponibles(getRecipients());

  // Se revisa contra TODOS los destinatarios: una columna puede existir en la cabecera
  // del CSV y venir vacía en algunas filas.
  const revisionMapeo = revisarMapeo(varMapping, getRecipients());

  /**
   * Pide al backend cuántos destinatarios quedan excluidos y por qué.
   *
   * LA POLÍTICA NO SE REPLICA AQUÍ. Este componente no sabe qué hace que alguien esté
   * permitido: manda los teléfonos y pinta lo que le responden. Contar en el frontend
   * exigiría aplicar la regla, y aplicar la regla es duplicar la política.
   *
   * Un fallo NO bloquea el lanzamiento: el motor aplica la política igual, y que una
   * vista informativa no funcione no puede tumbar la función principal.
   */
  const pedirPreview = async () => {
    const destinatarios = getRecipients();
    if (!selectedTemplate || destinatarios.length === 0) { setPreview(null); return; }

    setPreviewCargando(true);
    try {
      const r = await campaignsAPI.previewRecipients({
        organizationId: orgId,
        // La categoría la resuelve el servidor contra Meta; esto viaja sólo para que
        // pueda registrar si lo que la UI creía no era lo que Meta dice.
        templateCategory: selectedTemplate.category || null,
        templateName: selectedTemplate.name,
        templateLanguage: selectedTemplate.language,
        phoneNumberId: selectedNumero?.phone_number_id,
        telefonos: destinatarios.map((d) => d.phone),
      });
      setPreview(r);
    } catch (err) {
      console.error('Error al calcular el preview de destinatarios:', err);
      setPreview({ error: true });
    } finally {
      setPreviewCargando(false);
    }
  };

  const handleLaunchCampaign = async () => {
    if (!campaignName || !selectedTemplate || !selectedNumero) return;
    // Segunda red: el botón ya está deshabilitado, pero si alguien reordena el JSX
    // esto impide que salga una campaña con un hueco en el mensaje.
    if (!revisarMapeo(varMapping, getRecipients()).ok) return;
    const recipients = getRecipients();
    if (recipients.length === 0) {
      alert('Agrega al menos un destinatario');
      return;
    }

    setLaunching(true);
    try {
      // Build recipients with resolved components
      const recipientsWithComponents = recipients.map(r => ({
        phone: r.phone,
        nombre: r.nombre,
        components: buildRecipientComponents(r)
      }));

      // Create campaign document
      const campaignData = {
        name: campaignName,
        templateName: selectedTemplate.name,
        templateLanguage: selectedTemplate.language,
        // La CATEGORÍA se persiste con la campaña. Estaba disponible aquí desde el
        // Paso 4a —el listado la pinta— y se perdía justo al componer el documento,
        // así que el motor no sabía qué categoría enviaba. Es la quinta vez que
        // aparece el mismo patrón: un dato que existe arriba y no llega abajo.
        // `null` explícito si Meta no la devolvió: ausente y desconocida no son lo
        // mismo, y el filtro de consentimiento va a distinguirlas.
        templateCategory: selectedTemplate.category || null,
        phoneNumberId: selectedNumero.phone_number_id,
        wabaId: selectedNumero.waba_id,
        recipients: recipientsWithComponents,
        defaultComponents: [],
        totalRecipients: recipientsWithComponents.length,
        enviados: 0,
        fallidos: 0,
        progreso: 0,
        status: sendMode === 'now' ? 'borrador' : 'borrador',
        createdAt: serverTimestamp(),
        createdByUid: uid,
        organizationId: orgId
      };

      const docRef = await addDoc(collection(db, ...getCampaignsPath()), campaignData);

      if (sendMode === 'schedule' && scheduledDate && scheduledTime) {
        const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`);
        await campaignsAPI.schedule({
          campaignId: docRef.id,
          organizationId: orgId,
          scheduledAt: scheduledAt.toISOString()
        });
      } else {
        // Execute immediately.
        //
        // NO se espera a que termine: una campaña de 2.000 destinatarios tarda minutos
        // y bloquear la UI sería peor. Pero el error SÍ se recoge — antes se tragaba en
        // un `console.error`, así que un 409 «ya se está ejecutando» era invisible.
        campaignsAPI.execute({
          campaignId: docRef.id,
          organizationId: orgId
        }).catch((err) => {
          console.error('Campaign execution error:', err);
          // «Ya se está ejecutando» NO es un fallo: si se mostrara como error, el
          // operador reintentaría, que es justo lo que la transacción impide.
          const yaVa = /ya se está ejecutando|ya_en_ejecucion/i.test(err?.message || '');
          setAvisoEjecucion({
            tipo: yaVa ? 'info' : 'error',
            texto: yaVa ? t('list.yaEnEjecucion') : t('list.errorEjecucion', { motivo: err?.message || '' }),
          });
        });
      }

      // Reset wizard and go to list
      resetWizard();
      setView('list');
    } catch (err) {
      console.error('Error launching campaign:', err);
      alert('Error al lanzar la campaña: ' + err.message);
    } finally {
      setLaunching(false);
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      await updateDoc(doc(db, ...getCampaignsPath(), campaignId), { status: 'pausada' });
    } catch (err) {
      console.error('Error pausing campaign:', err);
    }
  };

  const exportResults = (campaign) => {
    if (!campaign.results?.length) return;
    // LA BASE JURÍDICA VA AQUÍ Y NO EN LA VISTA PREVIA, a propósito: el paso 3 del
    // asistente contesta «¿va a salir esto?», y la base es forense — cargaría el
    // momento de decidir con información que no ayuda a decidir. Esta exportación es
    // justamente donde miraría alguien auditando.
    const csv = ['telefono,estado,entrega,base,origenBase,error,messageId'];
    campaign.results.forEach(r => {
      const e = (r.messageId && entregas.porWamid[r.messageId]) || null;
      csv.push([
        r.phone, r.status,
        e?.estado || '',
        e?.base?.tipo || '',
        e?.base?.origen || '',
        (r.error || '').replace(/[,\n]/g, ' '),
        r.messageId || '',
      ].join(','));
    });
    const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaña_${campaign.name}_resultados.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const resetWizard = () => {
    setWizardStep(1);
    setCampaignName('');
    setSelectedTemplate(null);
    setVarMapping({});
    setRecipientMode('contacts');
    setSelectedContacts([]);
    setCsvRecipients([]);
    setSendMode('now');
    setScheduledDate('');
    setScheduledTime('');
  };

  const startCreateFlow = () => {
    resetWizard();
    setView('create');
    loadTemplates();
    loadContacts();
  };

  if (loading || contextLoading) return <div style={{ padding: '2rem', textAlign: 'center' }}>{tc('loading')}</div>;

  const filteredContacts = contacts.filter(c =>
    !contactSearch || c.nombre?.toLowerCase().includes(contactSearch.toLowerCase()) || c.phone?.includes(contactSearch)
  );

  return (
    <div style={{ padding: 'clamp(12px, 3vw, 32px)', maxWidth: '1000px', margin: '0 auto', boxSizing: 'border-box', width: '100%' }}>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      {/* El aviso de la ejecución. Llega tarde a propósito: el envío no se espera, así
          que el operador ya está en la lista cuando el backend responde. `info` para
          «ya se está ejecutando» y `error` para lo demás — confundirlos haría que
          reintentara algo que ya está en marcha. */}
      {view === 'list' && avisoEjecucion && (
        <div style={{
          marginBottom: '1rem', padding: '0.75rem', borderRadius: '8px',
          backgroundColor: avisoEjecucion.tipo === 'info' ? '#e8f0fe' : '#fce8e6',
          border: `1px solid ${avisoEjecucion.tipo === 'info' ? '#1a73e8' : '#f5c2c0'}`,
          color: avisoEjecucion.tipo === 'info' ? '#1a4d8f' : '#c5221f',
          fontSize: '0.88rem', display: 'flex', justifyContent: 'space-between', gap: '1rem',
        }}>
          <span>{avisoEjecucion.texto}</span>
          <button
            type="button"
            onClick={() => setAvisoEjecucion(null)}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit' }}
          >
            ✕
          </button>
        </div>
      )}

      {view === 'list' && (
        <div style={{ marginBottom: '1rem' }}>
          <button
            onClick={startCreateFlow}
            disabled={numerosApi.length === 0}
            style={{
              padding: '0.625rem 1.25rem',
              backgroundColor: numerosApi.length === 0 ? '#ccc' : '#1a73e8',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: numerosApi.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}
          >
            {t('newCampaign')}
          </button>
        </div>
      )}

      {numerosApi.length === 0 && view === 'list' && (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          backgroundColor: '#fff3cd',
          borderRadius: '8px',
          border: '1px solid #ffc107',
          color: '#856404'
        }}>
          <p style={{ fontWeight: '600' }}>{t('connectFirst.title')}</p>
          <p style={{ fontSize: '0.9rem' }}>
            {t('connectFirst.descriptionBefore')}<span onClick={() => navigate('/canales')} style={{ color: '#1a73e8', cursor: 'pointer', textDecoration: 'underline' }}>{t('connectFirst.channelsLink')}</span>{t('connectFirst.descriptionAfter')}
          </p>
        </div>
      )}

      {/* Campaign List View */}
      {view === 'list' && campaigns.length === 0 && numerosApi.length > 0 && (
        <div style={{
          padding: '3rem',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px dashed #dee2e6',
          color: '#6c757d'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📢</div>
          <p style={{ fontSize: '1.1rem' }}>{t('empty.title')}</p>
          <p style={{ fontSize: '0.9rem' }}>{t('empty.description')}</p>
        </div>
      )}

      {view === 'list' && campaigns.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem 1rem', fontWeight: '600', fontSize: '0.85rem', color: '#555' }}>{t('table.name')}</th>
                <th style={{ padding: '0.75rem 1rem', fontWeight: '600', fontSize: '0.85rem', color: '#555' }}>{t('table.template')}</th>
                <th style={{ padding: '0.75rem 1rem', fontWeight: '600', fontSize: '0.85rem', color: '#555' }}>{t('table.recipients')}</th>
                <th style={{ padding: '0.75rem 1rem', fontWeight: '600', fontSize: '0.85rem', color: '#555' }}>{t('table.status')}</th>
                <th style={{ padding: '0.75rem 1rem', fontWeight: '600', fontSize: '0.85rem', color: '#555' }}>{t('table.sent')}</th>
                <th style={{ padding: '0.75rem 1rem', fontWeight: '600', fontSize: '0.85rem', color: '#555' }}>{t('table.date')}</th>
                <th style={{ padding: '0.75rem 1rem' }}></th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(c => {
                const statusCfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.borrador;
                return (
                  <tr key={c.id} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: '500' }}>{c.name}</td>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.9rem', color: '#555' }}>{c.templateName}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>{c.totalRecipients || c.recipients?.length || 0}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{
                        padding: '3px 10px',
                        backgroundColor: statusCfg.bg,
                        color: statusCfg.color,
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: '600'
                      }}>
                        {t(statusCfg.labelKey)}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      {c.status === 'enviando' ? (
                        <span>{c.enviados || 0}/{c.totalRecipients || 0} ({c.progreso || 0}%)</span>
                      ) : (
                        <span>{c.enviados || 0}/{c.totalRecipients || 0}</span>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.85rem', color: '#888' }}>
                      {c.createdAt?.toDate?.()?.toLocaleDateString() || '-'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <button
                        onClick={() => { setSelectedCampaign(c); setView('detail'); }}
                        style={{
                          padding: '0.3rem 0.7rem',
                          backgroundColor: '#1a73e8',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        {t('table.view')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Campaign Detail View */}
      {view === 'detail' && selectedCampaign && (
        <div>
          <button
            onClick={() => { setView('list'); setSelectedCampaign(null); }}
            style={{ background: 'none', border: 'none', color: '#1a73e8', cursor: 'pointer', padding: 0, marginBottom: '1rem', fontSize: '0.9rem' }}
          >
            {t('detail.backToCampaigns')}
          </button>

          <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0 }}>{selectedCampaign.name}</h2>
              <span style={{
                padding: '4px 12px',
                backgroundColor: (STATUS_CONFIG[selectedCampaign.status] || STATUS_CONFIG.borrador).bg,
                color: (STATUS_CONFIG[selectedCampaign.status] || STATUS_CONFIG.borrador).color,
                borderRadius: '12px',
                fontWeight: '600'
              }}>
                {t((STATUS_CONFIG[selectedCampaign.status] || STATUS_CONFIG.borrador).labelKey)}
              </span>
            </div>

            {/* El porqué, cuando la campaña no terminó. Sin esto el operador ve un
                estado raro y ninguna explicación: `interrumpida` con 40 de 200 enviados
                necesita decir esos números para que decida si relanza al resto. */}
            {(selectedCampaign.motivoInterrupcion || selectedCampaign.errorBloqueo) && (
              <div style={{
                padding: '0.75rem', marginBottom: '1rem', borderRadius: '8px',
                backgroundColor: '#fef7e0', border: '1px solid #f0d48a',
                color: '#8a6d00', fontSize: '0.88rem',
              }}>
                {selectedCampaign.motivoInterrupcion || selectedCampaign.errorBloqueo}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>{t('detail.templateLabel')}</div>
                <div style={{ fontWeight: '600' }}>{selectedCampaign.templateName}</div>
              </div>
              <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>{t('detail.totalRecipients')}</div>
                <div style={{ fontWeight: '600' }}>{selectedCampaign.totalRecipients || 0}</div>
              </div>
              <div style={{ padding: '1rem', backgroundColor: '#d4edda', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.8rem', color: '#155724', marginBottom: '0.25rem' }}>{t('detail.sent')}</div>
                <div style={{ fontWeight: '600', color: '#155724' }}>{selectedCampaign.enviados || 0}</div>
              </div>
              <div style={{ padding: '1rem', backgroundColor: '#f8d7da', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.8rem', color: '#721c24', marginBottom: '0.25rem' }}>{t('detail.failed')}</div>
                <div style={{ fontWeight: '600', color: '#721c24' }}>{selectedCampaign.fallidos || 0}</div>
              </div>
              {/* ENTREGADOS SE AÑADE, no sustituye a «enviados».
                  «Enviado» es «Meta lo aceptó»; «entregado» es «llegó al teléfono». Son
                  cosas distintas y confundirlas borraría justo la distinción que este
                  dato existe para crear. */}
              <div style={{ padding: '1rem', backgroundColor: '#e6f4ea', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.8rem', color: '#137333', marginBottom: '0.25rem' }}>{t('detail.delivered')}</div>
                <div style={{ fontWeight: '600', color: '#137333' }}>
                  {entregas.cargando ? '…' : entregas.fallo ? '—' : (entregas.conteo.entregado || 0)}
                </div>
              </div>
            </div>

            {/* LOS ACUSES ANTERIORES AL DESPLIEGUE SON IRRECUPERABLES, y decirlo evita
                que un cero se lea como «no llegó nada». Los envíos que los originaron
                nunca dejaron documento, así que no hay dónde escribirlos: este dato
                cuenta desde el despliegue hacia adelante. Sólo se avisa cuando hay
                envíos pero ningún registro de entrega, que es exactamente ese caso. */}
            {!entregas.cargando && !entregas.fallo
              && (selectedCampaign.enviados || 0) > 0
              && Object.keys(entregas.conteo).length === 0 && (
              <div style={{
                padding: '0.75rem 1rem', marginBottom: '1.5rem', borderRadius: '8px',
                backgroundColor: '#f1f3f4', color: '#5f6368', fontSize: '0.85rem',
              }}>
                {t('detail.sinRegistroEntrega')}
              </div>
            )}

            {/* Progress bar */}
            {(selectedCampaign.status === 'enviando' || selectedCampaign.status === 'completada') && (
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                  <span>{t('detail.progress')}</span>
                  <span>{selectedCampaign.progreso || 0}%</span>
                </div>
                <div style={{ height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${selectedCampaign.progreso || 0}%`,
                    backgroundColor: selectedCampaign.status === 'completada' ? '#28a745' : '#1a73e8',
                    borderRadius: '4px',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {selectedCampaign.status === 'enviando' && (
                <button
                  onClick={() => handlePauseCampaign(selectedCampaign.id)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#dc3545',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  {t('detail.pause')}
                </button>
              )}
              {selectedCampaign.results?.length > 0 && (
                <button
                  onClick={() => exportResults(selectedCampaign)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  {t('detail.exportResults')}
                </button>
              )}
            </div>

            {/* Recipients list */}
            {selectedCampaign.results?.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <h3>{t('detail.recipientsTitle')}</h3>
                <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={{ padding: '0.5rem', textAlign: 'left', fontSize: '0.85rem' }}>{t('detail.phone')}</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', fontSize: '0.85rem' }}>{t('detail.statusLabel')}</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', fontSize: '0.85rem' }}>{t('detail.detailLabel')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCampaign.results.map((r, idx) => (
                        <tr key={idx} style={{ borderTop: '1px solid #eee' }}>
                          <td style={{ padding: '0.5rem', fontSize: '0.9rem' }}>{r.phone}</td>
                          <td style={{ padding: '0.5rem' }}>
                            {/* TRES estados, no dos. `bloqueado` es una decisión de la
                                política, no una avería: pintarlo como «fallido» haría
                                que el operador buscara un problema técnico donde hay
                                una regla haciendo su trabajo. */}
                            {r.status === 'enviado' ? (
                              <span style={{ color: '#155724' }}>{t('detail.statusSent')}</span>
                            ) : r.status === 'bloqueado' ? (
                              <span style={{ color: '#8a6d00' }}>{t('detail.statusBlocked')}</span>
                            ) : r.status === 'fallido' ? (
                              <span style={{ color: '#721c24' }}>{t('detail.statusFailed')}</span>
                            ) : (
                              <span style={{ color: '#856404' }}>{t('detail.statusPending')}</span>
                            )}
                          </td>
                          <td style={{ padding: '0.5rem', fontSize: '0.85rem', color: '#888' }}>
                            {/* En un bloqueo, el motivo traducido; el backend manda
                                además el texto en claro por si la clave no se conoce. */}
                            {/* EL WAMID CRUDO YA NO SE PINTA. Era un identificador de
                                Meta que no significaba nada para el operador y ocupaba
                                el sitio del dato que sí importa: si llegó. */}
                            {r.status === 'bloqueado' && r.motivo
                              ? t(`wizard.motivoBloqueo.${r.motivo}`, { defaultValue: r.error || r.motivo })
                              : r.error
                                ? r.error
                                : r.messageId && entregas.porWamid[r.messageId]
                                  ? t(`detail.entrega.${entregas.porWamid[r.messageId].estado}`)
                                  : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Campaign Wizard */}
      {view === 'create' && (
        <div>
          <button
            onClick={() => { setView('list'); resetWizard(); }}
            style={{ background: 'none', border: 'none', color: '#1a73e8', cursor: 'pointer', padding: 0, marginBottom: '1rem', fontSize: '0.9rem' }}
          >
            {t('wizard.cancel')}
          </button>

          {/* Wizard steps indicator */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
            {[1, 2, 3].map(step => (
              <div key={step} style={{
                flex: 1,
                height: '4px',
                borderRadius: '2px',
                backgroundColor: wizardStep >= step ? '#1a73e8' : '#e0e0e0'
              }} />
            ))}
          </div>

          <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>

            {/* Step 1: Configuration */}
            {wizardStep === 1 && (
              <div>
                <h2 style={{ marginTop: 0 }}>{t('wizard.step1Title')}</h2>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>{t('wizard.campaignName')}</label>
                  <input
                    type="text"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder={t('wizard.campaignNamePlaceholder')}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                  />
                </div>

                {numerosApi.length > 1 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>{t('wizard.whatsappNumber')}</label>
                    <select
                      value={selectedNumero?.id || ''}
                      onChange={(e) => {
                        const num = numerosApi.find(n => n.id === e.target.value) || null;
                        setSelectedNumero(num);
                        setSelectedTemplate(null);
                        // Con el número en mano: el estado aún no cambió (9b).
                        loadTemplates(num);
                        loadContacts(num);
                      }}
                      style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ccc' }}
                    >
                      {/* 9b: con más de un número no hay número por defecto. Sin esta opción vacía el
                          <select> pintaba el primero como elegido mientras el estado era null, y
                          elegirlo no disparaba onChange. */}
                      <option value="" disabled>{t('wizard.chooseNumber', 'Elige el número desde el que sale la campaña')}</option>
                      {numerosApi.map(n => (
                        <option key={n.id} value={n.id}>{n.display_phone_number || n.numero}</option>
                      ))}
                    </select>
                    {!selectedNumero && (
                      <div style={{ marginTop: '0.5rem', color: '#8a6d00', fontSize: '0.9rem' }}>
                        {t('wizard.chooseNumberHint', 'Los contactos y las plantillas son los de la cuenta del número elegido: elige uno para verlos.')}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>{t('wizard.templateLabel')}</label>

                  {/* U3 · al crear, `loadTemplates` refresca el listado y la plantilla
                      nueva aparece con su estado sin que el usuario recargue. El backend
                      ya invalidó la caché, así que esa lectura va a Graph. */}
                  <div style={{ marginBottom: '0.75rem' }}>
                    <CrearPlantilla
                      numero={selectedNumero}
                      organizationId={orgId}
                      onCreada={loadTemplates}
                    />
                  </div>

                  {loadingTemplates ? (
                    <p style={{ color: '#666' }}>{t('wizard.loadingTemplates')}</p>
                  ) : templates.length === 0 ? (
                    <p style={{ color: '#999' }}>{t('wizard.noTemplates')}</p>
                  ) : (
                    <div style={{ display: 'grid', gap: '8px', maxHeight: '300px', overflow: 'auto' }}>
                      {templates.map((tpl, idx) => {
                        const bodyComp = tpl.components?.find(c => c.type === 'BODY');
                        const isSelected = selectedTemplate?.name === tpl.name && selectedTemplate?.language === tpl.language;

                        // El nivel manda sobre TODO lo interactivo de la fila. Una fila
                        // bloqueada no lleva `onClick`, ni cursor de mano, ni `role`: no
                        // es un boton que falla, es texto que explica por que no se puede.
                        // `components` entra en la decisión: una MARKETING sin FOOTER no
                        // lleva vía de baja, y el servidor la rechazaría al enviar. Verlo
                        // aquí es lo que evita montar la campaña entera para nada.
                        const nivel = nivelDePlantilla(tpl.status, tpl.category, tpl.components);
                        const motivoBloqueo = motivoDeBloqueo(tpl.status, tpl.category, tpl.components);
                        const porCategoria = motivoBloqueo === MOTIVO_BLOQUEO.CATEGORIA;
                        const sinViaDeBaja = motivoBloqueo === MOTIVO_BLOQUEO.SIN_VIA_DE_BAJA;
                        const bloqueada = nivel === NIVEL.BLOQUEADO;
                        const motivo = motivoDeRechazo(tpl);
                        const calidad = puntuacionDeCalidad(tpl);
                        const etiquetaEstado = t(`wizard.templateStatus.${tpl.status}`, { defaultValue: tpl.status || '—' });

                        return (
                          <div
                            key={`${tpl.name}-${tpl.language}-${idx}`}
                            data-testid="fila-plantilla"
                            data-estado={tpl.status || ''}
                            data-nivel={nivel}
                            data-seleccionable={bloqueada ? 'no' : 'si'}
                            aria-disabled={bloqueada ? 'true' : undefined}
                            onClick={bloqueada ? undefined : () => handleSelectTemplate(tpl)}
                            style={{
                              padding: '0.75rem',
                              border: isSelected ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                              borderRadius: '8px',
                              cursor: bloqueada ? 'not-allowed' : 'pointer',
                              backgroundColor: isSelected ? '#e8f0fe' : bloqueada ? '#fafafa' : '#fff',
                              opacity: bloqueada ? 0.65 : 1
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <strong>{tpl.name}</strong>
                              <span style={{ fontSize: '0.75rem', color: '#666' }}>({tpl.language})</span>

                              {/* La CATEGORIA decide lo que el cliente paga por envio en su
                                  propia cuenta de Meta, y hasta ahora no se veia en ningun sitio. */}
                              {tpl.category && (
                                <span style={{ fontSize: '0.7rem', color: '#3c4043', backgroundColor: '#f1f3f4', border: '1px solid #dadce0', borderRadius: '10px', padding: '0.1rem 0.45rem' }}>
                                  {t(`wizard.templateCategory.${tpl.category}`, { defaultValue: tpl.category })}
                                </span>
                              )}

                              {/* El estado solo se pinta cuando NO es APPROVED: en una lista
                                  de aprobadas, repetir "Aprobada" en cada fila es ruido.
                                  Y NO se pinta en la que le falta el pie: está APPROVED de
                                  verdad, así que la insignia diría «Aprobada» justo al lado
                                  de «no se puede elegir» — dos mensajes que se contradicen a
                                  ojos del operador. El motivo lo explica el texto de abajo. */}
                              {nivel !== NIVEL.SELECCIONABLE && !sinViaDeBaja && (
                                <span style={{ fontSize: '0.7rem', fontWeight: 600, color: bloqueada ? '#5f6368' : '#8a6d00', backgroundColor: bloqueada ? '#e8eaed' : '#fef7e0', borderRadius: '10px', padding: '0.1rem 0.45rem' }}>
                                  {etiquetaEstado}
                                </span>
                              )}

                              {calidad && (
                                <span style={{ fontSize: '0.7rem', color: '#5f6368' }}>
                                  {t('wizard.templateQuality', { defaultValue: 'Calidad' })}: {calidad}
                                </span>
                              )}
                            </div>

                            {bodyComp?.text && (
                              <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '0.25rem', maxHeight: '40px', overflow: 'hidden' }}>
                                {bodyComp.text}
                              </div>
                            )}

                            {/* Por que no se puede elegir, o por que hay que tener cuidado.
                                Sin esto la fila bloqueada seria igual de opaca que el filtro
                                que la escondia. */}
                            {nivel !== NIVEL.SELECCIONABLE && (
                              <div style={{ fontSize: '0.78rem', color: bloqueada ? '#5f6368' : '#8a6d00', marginTop: '0.35rem' }}>
                                {/* El orden importa: una MARKETING sin pie está APPROVED,
                                    así que la rama del estado diría «Aprobada» y no
                                    explicaría nada. La razón real va primero. */}
                                {porCategoria
                                  ? t('wizard.categoriaNoEnviable')
                                  : sinViaDeBaja
                                    ? t('wizard.sinViaDeBaja')
                                    : t(`wizard.templateStatusHelp.${tpl.status}`, {
                                    defaultValue: t('wizard.templateStatusHelpUnknown', { defaultValue: 'Esta plantilla no se puede usar en una campaña.' }),
                                  })}
                                {motivo && (
                                  <div style={{ marginTop: '0.2rem' }}>
                                    {t('wizard.templateRejectedReason', { defaultValue: 'Motivo de Meta' })}: <code>{motivo}</code>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Variable mapping */}
                {selectedTemplate && Object.keys(varMapping).length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>{t('wizard.templateVariables')}</label>
                    {Object.entries(varMapping).sort(([a], [b]) => Number(a) - Number(b)).map(([key, config]) => (
                      <div key={key} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                        <span style={{ minWidth: '60px', fontSize: '0.85rem' }}>{`{{${key}}}`}</span>
                        <select
                          value={config.type}
                          onChange={(e) => setVarMapping(prev => ({
                            ...prev,
                            [key]: { ...prev[key], type: e.target.value, value: '' }
                          }))}
                          style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc' }}
                        >
                          <option value="fixed">{t('wizard.fixedValue')}</option>
                          <option value="field">{t('wizard.contactField')}</option>
                        </select>
                        {config.type === 'fixed' ? (
                          <input
                            type="text"
                            value={config.value}
                            onChange={(e) => setVarMapping(prev => ({
                              ...prev,
                              [key]: { ...prev[key], value: e.target.value }
                            }))}
                            placeholder={t('wizard.fixedPlaceholder')}
                            style={{ flex: 1, padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc' }}
                          />
                        ) : (
                          <select
                            value={config.value}
                            onChange={(e) => setVarMapping(prev => ({
                              ...prev,
                              [key]: { ...prev[key], value: e.target.value }
                            }))}
                            style={{ flex: 1, padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc' }}
                          >
                            <option value="">{t('wizard.selectField')}</option>
                            {/* Sólo los campos que el ORIGEN tiene. `empresa` estaba fijo
                                aquí y no existe para los contactos de la lista: elegirlo
                                daba un valor vacío que acababa en un espacio. */}
                            {camposParaVariables.map((campo) => (
                              <option key={campo} value={campo}>
                                {campo === 'nombre' ? t('wizard.fieldName') : campo}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button
                    onClick={() => setWizardStep(2)}
                    disabled={!campaignName || !selectedTemplate}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: (!campaignName || !selectedTemplate) ? '#ccc' : '#1a73e8',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (!campaignName || !selectedTemplate) ? 'not-allowed' : 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    {t('wizard.nextStep')}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Recipients */}
            {wizardStep === 2 && (
              <div>
                <h2 style={{ marginTop: 0 }}>{t('wizard.step2Title')}</h2>

                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                  <button
                    onClick={() => setRecipientMode('contacts')}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      border: recipientMode === 'contacts' ? '2px solid #1a73e8' : '1px solid #ccc',
                      borderRadius: '8px',
                      backgroundColor: recipientMode === 'contacts' ? '#e8f0fe' : '#fff',
                      cursor: 'pointer',
                      fontWeight: recipientMode === 'contacts' ? '600' : '400'
                    }}
                  >
                    {t('wizard.selectContacts')}
                  </button>
                  <button
                    onClick={() => setRecipientMode('csv')}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      border: recipientMode === 'csv' ? '2px solid #1a73e8' : '1px solid #ccc',
                      borderRadius: '8px',
                      backgroundColor: recipientMode === 'csv' ? '#e8f0fe' : '#fff',
                      cursor: 'pointer',
                      fontWeight: recipientMode === 'csv' ? '600' : '400'
                    }}
                  >
                    {t('wizard.uploadCsv')}
                  </button>
                </div>

                {recipientMode === 'contacts' && (
                  <div>
                    <input
                      type="text"
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder={t('wizard.searchContact')}
                      style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ccc', marginBottom: '0.75rem', boxSizing: 'border-box' }}
                    />

                    <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #eee', borderRadius: '6px' }}>
                      {filteredContacts.length === 0 && (
                        <p style={{ textAlign: 'center', color: '#999', padding: '1rem' }}>
                          {t('wizard.noContactsFound')}
                        </p>
                      )}
                      {filteredContacts.map(c => {
                        const isSelected = selectedContacts.some(sc => sc.id === c.id);
                        // Sin teléfono resoluble NO se puede seleccionar: `executeCampaign`
                        // no sabe enviar a un contacto sin número, y dejar marcarlo sólo
                        // aplazaría el fallo hasta después de lanzar.
                        const bloqueado = !c.phone;
                        return (
                          <div
                            key={c.id}
                            data-testid="fila-contacto"
                            data-seleccionable={bloqueado ? 'no' : 'si'}
                            aria-disabled={bloqueado ? 'true' : undefined}
                            onClick={bloqueado ? undefined : () => {
                              if (isSelected) {
                                setSelectedContacts(prev => prev.filter(sc => sc.id !== c.id));
                              } else {
                                setSelectedContacts(prev => [...prev, c]);
                              }
                            }}
                            style={{
                              padding: '0.5rem 0.75rem',
                              borderBottom: '1px solid #f0f0f0',
                              cursor: bloqueado ? 'not-allowed' : 'pointer',
                              backgroundColor: isSelected ? '#e8f0fe' : bloqueado ? '#fafafa' : '#fff',
                              opacity: bloqueado ? 0.6 : 1,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: '500' }}>{c.nombre}</div>
                              {/* El teléfono, que es lo que hay que ver antes de disparar un
                                  envío masivo. Antes salía el nombre otra vez, porque ambos
                                  campos venían de `remitente`. */}
                              <div style={{ fontSize: '0.85rem', color: bloqueado ? '#c5221f' : '#666' }}>
                                {c.phone || t(`wizard.contactoMotivo.${c.motivo}`, { defaultValue: t('wizard.contactoSinTelefono') })}
                              </div>
                            </div>
                            {isSelected && <span style={{ color: '#1a73e8', fontWeight: '700' }}>✓</span>}
                          </div>
                        );
                      })}
                    </div>

                    <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                      {t('wizard.contactsSelected', { count: selectedContacts.length })}
                    </p>
                  </div>
                )}

                {recipientMode === 'csv' && (
                  <div>
                    <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.75rem' }} dangerouslySetInnerHTML={{ __html: t('wizard.csvInstructions') }} />

                    {/* TODO ESTO VA ANTES DEL FICHERO, a propósito: si fuera después,
                        el operador subiría el CSV, vería «200 destinatarios» y sólo
                        entonces descubriría que no puede enviarles. Marcándolo antes,
                        la declaración es una decisión previa al acto.

                        EL ORDEN LO FIJÓ LA REVISIÓN LEGAL: las finalidades primero, la
                        declaración inmediatamente después. El texto dice «para las
                        finalidades seleccionadas», así que leerlo antes de elegirlas
                        sería leer una frase que todavía no se refiere a nada. */}
                    <div style={{
                      padding: '0.75rem', marginBottom: '0.5rem',
                      backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0',
                      borderRadius: '8px 8px 0 0', borderBottom: 'none',
                    }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#3c4043', marginBottom: '0.5rem' }}>
                        {t('wizard.finalidadesTitulo')}
                      </div>
                      {FINALIDADES_UI.map((f) => (
                        <label key={f} style={{
                          display: 'flex', gap: '0.6rem', alignItems: 'center',
                          padding: '0.25rem 0', cursor: 'pointer',
                          fontSize: '0.85rem', color: '#3c4043',
                        }}>
                          <input
                            type="checkbox"
                            checked={finalidades.includes(f)}
                            onChange={(e) => setFinalidades((prev) => (e.target.checked
                              ? [...prev, f]
                              : prev.filter((x) => x !== f)))}
                          />
                          <span>{t(`wizard.finalidad.${f}`)}</span>
                        </label>
                      ))}
                    </div>

                    <label style={{
                      display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                      padding: '0.75rem', marginBottom: '1rem',
                      backgroundColor: declaracionAceptada ? '#e8f0fe' : '#f8f9fa',
                      border: `1px solid ${declaracionAceptada ? '#1a73e8' : '#e0e0e0'}`,
                      borderRadius: '0 0 8px 8px', cursor: 'pointer',
                    }}>
                      <input
                        type="checkbox"
                        checked={declaracionAceptada}
                        onChange={(e) => setDeclaracionAceptada(e.target.checked)}
                        style={{ marginTop: '0.2rem' }}
                      />
                      <span style={{ fontSize: '0.85rem', color: '#3c4043', lineHeight: 1.45 }}>
                        <strong style={{ display: 'block', marginBottom: '0.35rem' }}>
                          {t('wizard.declaracionTitulo')}
                        </strong>
                        {/* `white-space: pre-line` para que los tres párrafos del texto
                            legal se vean como tres párrafos y no como un muro. */}
                        <span style={{ whiteSpace: 'pre-line' }}>{t('wizard.declaracionTexto')}</span>
                      </span>
                    </label>

                    {/* EL SELECTOR DE FICHERO, BLOQUEADO HASTA ACEPTAR. Antes se podía
                        subir el CSV sin marcar nada: los contactos se cargaban, no se
                        registraba declaración, y el operador descubría el bloqueo al
                        lanzar. Ahora el orden es el que pide la recomendación legal. */}
                    <input
                      ref={csvInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleCsvUpload}
                      disabled={!puedeImportar}
                      aria-label={t('wizard.uploadCsv')}
                      style={{ marginBottom: '0.5rem', opacity: puedeImportar ? 1 : 0.5 }}
                    />
                    {!puedeImportar && (
                      <p style={{ fontSize: '0.8rem', color: '#8a6d00', marginBottom: '1rem' }}>
                        {finalidades.length === 0
                          ? t('wizard.faltaFinalidad')
                          : t('wizard.faltaDeclaracion')}
                      </p>
                    )}

                    {declarando && (
                      <p style={{ fontSize: '0.85rem', color: '#666' }}>{t('wizard.declaracionRegistrando')}</p>
                    )}

                    {/* NO DICE «hubo un error». Dice QUÉ no pasó y QUÉ consecuencia
                        tiene, porque «hubo un error» deja al operador subiendo el
                        fichero otra vez sin saber que el problema no es el fichero. */}
                    {falloDeclaracion && (
                      <div role="alert" style={{
                        padding: '0.75rem', marginBottom: '1rem', borderRadius: '8px',
                        backgroundColor: '#fce8e6', border: '1px solid #f5c2c0', color: '#c5221f',
                        fontSize: '0.85rem',
                      }}>
                        {t('wizard.declaracionFallo')}
                      </div>
                    )}

                    {csvRecipients.length > 0 && (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#d4edda',
                        borderRadius: '6px',
                        color: '#155724'
                      }}>
                        {t('wizard.csvRecipientsFound', { count: csvRecipients.length })}
                      </div>
                    )}

                    {csvDescartadas.length > 0 && (
                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#fef7e0', border: '1px solid #f0d48a', borderRadius: '6px', color: '#8a6d00' }}>
                        <strong>{t('wizard.csvDescartadas', { count: csvDescartadas.length })}</strong>
                        <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem', maxHeight: '160px', overflow: 'auto' }}>
                          {csvDescartadas.map((d) => (
                            <li key={d.fila} style={{ fontSize: '0.85rem' }}>
                              {t('wizard.csvFila', { fila: d.fila })}: <code>{d.valor || '—'}</code>
                              {' — '}
                              {t(`wizard.contactoMotivo.${d.motivo}`, { defaultValue: t('wizard.contactoSinTelefono') })}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
                  <button
                    onClick={() => setWizardStep(1)}
                    style={{ padding: '0.75rem 1.5rem', border: '1px solid #ccc', borderRadius: '8px', backgroundColor: '#fff', cursor: 'pointer' }}
                  >
                    {t('wizard.previousStep')}
                  </button>
                  <button
                    onClick={() => { setWizardStep(3); pedirPreview(); }}
                    disabled={getRecipientCount() === 0}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: getRecipientCount() === 0 ? '#ccc' : '#1a73e8',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: getRecipientCount() === 0 ? 'not-allowed' : 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    {t('wizard.nextStep')}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Review & Send */}
            {wizardStep === 3 && (
              <div>
                <h2 style={{ marginTop: 0 }}>{t('wizard.step3Title')}</h2>

                {/* Summary */}
                <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1.5rem' }}>
                  <div style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                    <span style={{ color: '#666', fontSize: '0.85rem' }}>{t('wizard.summaryCampaign')} </span>
                    <strong>{campaignName}</strong>
                  </div>
                  <div style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                    <span style={{ color: '#666', fontSize: '0.85rem' }}>{t('wizard.summaryTemplate')} </span>
                    <strong>{selectedTemplate?.name}</strong>
                    <span style={{ fontSize: '0.85rem', color: '#888' }}> ({selectedTemplate?.language})</span>
                  </div>
                  <div style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                    <span style={{ color: '#666', fontSize: '0.85rem' }}>{t('wizard.summaryRecipients')} </span>
                    <strong>{getRecipientCount()}</strong>
                  </div>
                </div>

                {/* Preview */}
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#dcf8c6',
                  borderRadius: '8px',
                  borderLeft: '4px solid #25D366',
                  marginBottom: '1.5rem',
                  whiteSpace: 'pre-wrap'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#555', marginBottom: '0.5rem', fontWeight: '600' }}>
                    {t('wizard.messagePreview')}
                  </div>
                  {getTemplatePreview()}
                </div>

                {/* RECUENTO DE DESTINATARIOS · lo que el operador necesita ANTES de lanzar.
                    Todo lo que se pinta aquí viene del endpoint: este componente no
                    aplica la política, sólo la muestra. */}
                <div style={{ marginBottom: '1.5rem' }}>
                  {previewCargando && (
                    <p style={{ color: '#666', fontSize: '0.9rem' }}>{t('wizard.previewCargando')}</p>
                  )}

                  {!previewCargando && preview?.error && (
                    // C4 · un fallo del preview NO deshabilita «Lanzar campaña». El motor
                    // aplica la política igual; una vista informativa que no funciona no
                    // puede tumbar la función principal.
                    <div style={{ padding: '0.75rem', backgroundColor: '#fef7e0', border: '1px solid #f0d48a', borderRadius: '6px', color: '#8a6d00', fontSize: '0.85rem' }}>
                      {t('wizard.previewFallo')}
                    </div>
                  )}

                  {!previewCargando && preview && !preview.error && (
                    <div style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                      <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>
                        {/* «permitidos AHORA», nunca «se enviarán»: es una foto, y el
                            envío puede diferir porque el motor recarga en cada lote. */}
                        {t('wizard.previewPermitidos', { count: preview.permitidosAhora })}
                        {(preview.total - preview.permitidosAhora) > 0 && (
                          <> · {t('wizard.previewBloqueados', { count: preview.total - preview.permitidosAhora })}</>
                        )}
                      </div>

                      {/* Los motivos en lenguaje de negocio, no en claves internas. */}
                      <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.2rem', fontSize: '0.85rem', color: '#5f6368' }}>
                        {Object.entries(preview.bloqueados || {}).map(([motivo, n]) => (
                          <li key={motivo}>
                            {n} · {t(`wizard.motivoBloqueo.${motivo}`, { defaultValue: motivo })}
                            {/* C2 · los ejemplos dicen CUÁL es el problema, no sólo que lo hay. */}
                            {preview.ejemplos?.[motivo]?.length > 0 && (
                              <span style={{ color: '#80868b' }}>
                                {' — '}{preview.ejemplos[motivo].join(', ')}
                                {n > preview.muestraTruncadaEn && ` ${t('wizard.previewYMas', { count: n - preview.muestraTruncadaEn })}`}
                              </span>
                            )}
                          </li>
                        ))}
                        {preview.sinTelefonoValido > 0 && (
                          <li>{preview.sinTelefonoValido} · {t('wizard.motivoBloqueo.sin_telefono_valido')}</li>
                        )}
                      </ul>

                      {/* Los excluidos por una BAJA previa se dicen aparte: reimportar
                          NO los reactiva —la persona habló por sí misma y una
                          declaración del negocio no puede desdecirla— y sin este aviso
                          el operador creería que la importación falló y volvería a
                          importar, que tampoco funcionaría y esa vez sin explicación. */}
                      {preview.bloqueados?.consentimiento_retirado > 0 && (
                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: '#8a6d00' }}>
                          {t('wizard.previewExcluidosPorBaja', { count: preview.bloqueados.consentimiento_retirado })}
                        </p>
                      )}

                      {/* C3 · CONTEXTO, no aviso. Sin rojo ni icono de alerta: la ventana
                          razonable depende del negocio, no del software, y pintarlo como
                          alarma colaría por la UI la regla que decidimos no imponer. */}
                      {preview.antiguedad?.mas180 > 0 && (
                        <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: '#5f6368' }}>
                          {t('wizard.previewAntiguedad', { count: preview.antiguedad.mas180, total: preview.total })}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Send options */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.75rem' }}>{t('wizard.whenToSend')}</label>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input type="radio" checked={sendMode === 'now'} onChange={() => setSendMode('now')} />
                      {t('wizard.sendNow')}
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input type="radio" checked={sendMode === 'schedule'} onChange={() => setSendMode('schedule')} />
                      {t('wizard.schedule')}
                    </label>
                  </div>

                  {sendMode === 'schedule' && (
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
                      <input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc' }}
                      />
                      <input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc' }}
                      />
                    </div>
                  )}
                </div>

                {/* Warning */}
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '6px',
                  marginBottom: '1.5rem',
                  fontSize: '0.85rem',
                  color: '#856404'
                }}>
                  {t('wizard.warning')}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button
                    onClick={() => setWizardStep(2)}
                    style={{ padding: '0.75rem 1.5rem', border: '1px solid #ccc', borderRadius: '8px', backgroundColor: '#fff', cursor: 'pointer' }}
                  >
                    {t('wizard.previousStep')}
                  </button>
                  {/* El usuario ve QUÉ falta y para QUIÉN antes de lanzar, no después.
                      Enterarse de un hueco por el mensaje que recibió el cliente es lo
                      que este bloque impide. */}
                  {!revisionMapeo.ok && (
                    <div style={{ padding: '0.75rem', backgroundColor: '#fce8e6', border: '1px solid #f5c2c0', borderRadius: '6px', color: '#c5221f', fontSize: '0.85rem', flex: 1, marginRight: '1rem' }}>
                      <strong>{t('wizard.variablesIncompletas', { count: revisionMapeo.faltantes.length })}</strong>
                      <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem', maxHeight: '120px', overflow: 'auto' }}>
                        {revisionMapeo.faltantes.slice(0, 10).map((f, i) => (
                          <li key={`${f.indice}-${f.destinatario}-${i}`}>
                            {`{{${f.indice}}}`}
                            {f.destinatario ? ` — ${f.destinatario}` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <button
                    onClick={handleLaunchCampaign}
                    data-testid="boton-lanzar"
                    disabled={launching || !revisionMapeo.ok || (sendMode === 'schedule' && (!scheduledDate || !scheduledTime))}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: (launching || !revisionMapeo.ok) ? '#6c757d' : '#28a745',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (launching || !revisionMapeo.ok) ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '1rem'
                    }}
                  >
                    {launching ? t('wizard.launching') : sendMode === 'schedule' ? t('wizard.scheduleCampaign') : t('wizard.launchCampaign')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
